import styled, { css } from 'styled-components';

import { SessionStatus } from './helpers';

import { device, typography } from '@/styles/helpers';

export const ScProfileTitle = styled.p`
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: 0.02em;
  text-align: center;
  margin-bottom: 16px;
`;

export const ScProfileDescription = styled.p`
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 0.01em;
  text-align: center;
  margin-bottom: 24px;
  color: ${({ theme }) => theme.color2};
`;

export const ScProfileIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

export const ScProfileContainer = styled.div`
  padding: 24px 16px;
  border-radius: 16px;
  background: ${({ theme }) => theme.color1};

  & .button-container {
    min-width: 240px;
    max-width: max-content;
    margin: 0 auto;

    @media (${device.mobile}) {
      max-width: none;
    }
  }

  & .buttons-container {
    display: flex;
    gap: 8px;
    margin-top: 24px;
  }

  & .fields-container {
    display: flex;
    flex-direction: column;
    row-gap: 16px;
  }

  & .code-container {
    margin: 24px 0;
  }

  & .code-section {
    display: grid;
    grid-template-columns: auto 160px;
    gap: 8px;
    align-items: baseline;
  }
`;

export const ScSecurityPageContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (${device.mobile}) {
    grid-template-columns: repeat(1, 1fr);
  }

  & .left {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  & .right {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

export const ScSessionRowContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 0;

  &:not(:last-child) {
    border-bottom: ${({ theme }) => `1px solid ${theme.color43}`};
  }

  &:first-child {
    padding-top: 0;
  }
`;

export const ScSessionRowInfo = styled.div<{ isActive: boolean }>`
  display: grid;
  gap: 8px;
  opacity: ${({ isActive }) => (isActive ? 0.4 : 1)};

  & .status {
    display: flex;
    justify-content: space-between;
    & .cancel-btn {
      ${typography['body2bold']}
      color: ${({ theme }) => theme.color11};
      border: none;
      background: transparent;
      text-transform: capitalize;
      cursor: pointer;

      &:hover {
        color: ${({ theme }) => theme.color10};
      }
    }
  }

  & .top {
    display: flex;
    justify-content: space-between;
    align-items: baseline;

    & .device {
      display: flex;
      gap: 8px;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.01em;
      color: ${({ theme }) => theme.white};

      & .os {
        font-weight: 700;
      }
    }

    & .top-right {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  & .bottom {
    display: flex;
    gap: 8px;

    & .ip,
    & .country {
      font-size: 14px;
      line-height: 20px;
      color: ${({ theme }) => theme.white};
    }

    & .ip {
      font-weight: 700;
      letter-spacing: 0.01em;
    }

    & .country {
      font-weight: 400;
      letter-spacing: 0em;
    }
  }
`;

export const ScStatus = styled.div<{ status: string }>`
  display: flex;
  align-items: center;
  width: fit-content;
  padding: 0 12px;
  height: 24px;
  border-radius: 360px;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  letter-spacing: 0em;
  text-transform: capitalize;
  white-space: nowrap;

  color: ${({ theme, status }) => {
    switch (status) {
      case SessionStatus.Current:
      case SessionStatus.Active:
        return theme.success;
      case SessionStatus.Closed:
        return theme.error;
      default:
        return null;
    }
  }};

  background: ${({ theme, status }) => {
    switch (status) {
      case SessionStatus.Current:
      case SessionStatus.Active:
        return theme.successBg;
      case SessionStatus.Closed:
        return theme.errorBg;
      default:
        return null;
    }
  }};
`;

export const ScNoSessions = styled.p`
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 0.01em;
  text-align: center;
  color: ${({ theme }) => theme.white};
`;

export const ScConfirmationMessage = styled(ScNoSessions)`
  font-size: 16px;
  line-height: 24px;
`;

export const ScCloseSessionSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;

  & .buttons {
    display: flex;
    gap: 8px;
    margin-top: 16px;
  }
`;

export const ScTwoFactorMessage = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.01em;
  text-align: center;
  color: ${({ theme }) => theme.white};

  & a {
    color: ${({ theme }) => theme.color11};
    font-weight: 700;
    cursor: pointer;
    text-decoration: underline;

    &:hover {
      color: ${({ theme }) => theme.color10};
    }
  }
`;

export const ScQRCode = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 120px;
  height: 120px;
  border-radius: 8px;
  background: ${({ theme }) => theme.white};
  margin: 16px auto;
`;

export const ScBackupSection = styled.div`
  padding: 24px 0;
  margin-bottom: 24px;
  border-top: ${({ theme }) => `1px solid ${theme.color43}`};
  border-bottom: ${({ theme }) => `1px solid ${theme.color43}`};
`;

export const ScBackupMessage = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0em;
  text-align: center;
  color: ${({ theme }) => theme.white};
`;

export const ScBackupCodes = styled.div`
  display: grid;
  gap: 8px;
  margin: 16px 0;
`;

export const ScBackupCode = styled.p`
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 0.01em;
  text-align: center;
  color: ${({ theme }) => theme.white};
`;

export const ScCrossIcon = styled.div<{ disabled: boolean }>`
  height: 24px;
  width: 24px;
  cursor: pointer;

  ${({ disabled, theme }) =>
    disabled
      ? css`
          cursor: auto;
          pointer-events: none;
        `
      : css`
          &:hover svg path {
            fill: ${theme.white};
          }
        `}
`;
